import { memo, useState } from "react";

import classnames from "classnames";
import { Typography, Alert, Callout, Button, Spinner } from "neetoui";
import {
  Form as NeetoUIForm,
  Switch,
  Button as FormikButton,
  BlockNavigation,
} from "neetoui/formik";
import { Trans, useTranslation } from "react-i18next";

import CardLayout from "components/CardLayout";

import AllowedIpRanges from "./AllowedIpRanges";
import { VALIDATION_SCHEMA } from "./constants";
import CurrentIp from "./CurrentIp";
import { useFetchAllowedIpRanges } from "./hooks/reactQuery/useAllowedIpRanges";
import {
  useShowIpRestriction,
  useUpdateIpRestriction,
} from "./hooks/reactQuery/useIpRestrictions";
import "./IpRestriction.scss";
import { buildInitialValue, buildIpRestrictionPayload } from "./utils";

const IpRestriction = () => {
  const { t } = useTranslation();
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const {
    data: { isIpRestrictionEnabled } = {},
    isLoading: isFetchingIpRestriction,
  } = useShowIpRestriction();

  const {
    data: allowedIpRanges,
    isLoading: isFetchingAllowedIpRanges,
    isRefetching,
  } = useFetchAllowedIpRanges({ enabled: isIpRestrictionEnabled });

  const { mutate: updateIpRestriction, isPending: isUpdating } =
    useUpdateIpRestriction();

  if (isFetchingIpRestriction || isFetchingAllowedIpRanges || isRefetching) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const handleSubmit = (values, formikBag) => {
    if (values.ipRestriction) setIsAlertOpen(true);
    else handleUpdateIpRestriction(values, formikBag);
  };

  const handleCloseAlert = setSubmitting => {
    setIsAlertOpen(false);
    setSubmitting(false);
  };

  const handleUpdateIpRestriction = (values, { setSubmitting, resetForm }) => {
    updateIpRestriction(buildIpRestrictionPayload(values), {
      onSuccess: () => {
        setIsAlertOpen(false);
        setSubmitting(false);
        resetForm({ values });
      },
      onError: () => {
        setIsAlertOpen(false);
        setSubmitting(false);
      },
    });
  };

  return (
    <NeetoUIForm
      className="w-full"
      formikProps={{
        initialValues: buildInitialValue(
          isIpRestrictionEnabled,
          allowedIpRanges
        ),
        validationSchema: VALIDATION_SCHEMA,
        validateOnBlur: true,
        enableReinitialize: true,
        onSubmit: handleSubmit,
      }}
    >
      {({ values, dirty, setFieldValue, resetForm, setSubmitting }) => (
        <CardLayout
          actionBlock={
            <div className="flex flex-wrap items-center gap-2">
              <FormikButton
                data-testid="ip-restriction-save-changes"
                loading={isUpdating}
                type="submit"
              >
                {t("neetoMolecules.common.actions.saveChanges")}
              </FormikButton>
              <Button
                data-testid="ip-restriction-reset-btn"
                disabled={!dirty}
                label={t("neetoMolecules.common.actions.reset")}
                style="secondary"
                type="reset"
                onClick={resetForm}
              />
              {dirty && (
                <Typography className="neeto-ui-text-error-500" style="body2">
                  {t("neetoMolecules.ipRestriction.validations.unsavedChanges")}
                </Typography>
              )}
            </div>
          }
        >
          <BlockNavigation />
          <div
            className={classnames("flex flex-col gap-y-2", {
              "pb-6": values.ipRestriction,
            })}
          >
            <Switch
              checked={values.ipRestriction}
              className="flex-grow-0"
              data-testid="enable-ip-restriction-switch"
              disabled={isFetchingIpRestriction || isUpdating}
              id="ipRestriction"
              name="ipRestriction"
              label={
                <Typography style="h3">
                  {t("neetoMolecules.ipRestriction.switchLabel")}
                </Typography>
              }
              onChange={() =>
                setFieldValue("ipRestriction", !values.ipRestriction)
              }
            />
            <Typography data-testid="ip-restriction-description" style="body2">
              {t("neetoMolecules.ipRestriction.description")}
            </Typography>
          </div>
          {values.ipRestriction && (
            <div className="neeto-ui-border-gray-300 flex w-full flex-col gap-y-6 border-t pt-6">
              <CurrentIp />
              <AllowedIpRanges />
              <Callout style="warning">
                <div className="block whitespace-pre-wrap py-1">
                  <Trans i18nKey="neetoMolecules.ipRestriction.warningMessage" />
                </div>
              </Callout>
            </div>
          )}
          <Alert
            isOpen={isAlertOpen}
            isSubmitting={isUpdating}
            title={t("neetoMolecules.ipRestriction.enableAlert.title")}
            message={
              <Trans i18nKey="neetoMolecules.ipRestriction.enableAlert.message" />
            }
            submitButtonLabel={t(
              "neetoMolecules.ipRestriction.enableAlert.submitButtonLabel"
            )}
            onClose={() => handleCloseAlert(setSubmitting)}
            onSubmit={() =>
              handleUpdateIpRestriction(values, { setSubmitting, resetForm })
            }
          />
        </CardLayout>
      )}
    </NeetoUIForm>
  );
};

export default memo(IpRestriction);
